'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { actionCopy } from './action-copy';

import { useOptionalWorkspace } from '@/workspace/context';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { actionSurfaceCopy } from './action-surface-copy';
import { useActionConfirmation } from './use-action-confirmation';
import { ActionInline } from './action-inline';
import { ActionCanvasReview } from './action-canvas-review';

export type ActionOrigin = { messageId: string; toolCallId: string };

export type ActionPointer = {
  status: 'confirmation_required' | 'confirmation_already_pending' | 'build_requested';
  action: { id: string };
};
export type ActionDecision = {
  decision: string;
  source: string;
  reasons: string[];
  versions: Record<string, string>;
  taint?: { flagged: boolean; sources: string[] };
  /**
   * A connected call's human presentation (connect-time access C18), from the server: the
   * connection's name, the action's current provider title, schema titles for the keys of
   * `args.arguments`, and whether the provider labels it a read. Display only.
   */
  connection?: {
    name: string;
    title: string;
    fields: Record<string, string>;
    isRead: boolean;
    /** The sending account's label, on a card action (the email draft). */
    account?: string | null;
    /** A reply whose provider keeps the conversation's subject (Gmail). */
    replyKeepsSubject?: boolean;
    needsWriteAccess?: boolean;
    canChangeAccess?: boolean;
  };
};
export type ActionView = {
  id: string;
  tool: string;
  label: string;
  args: Record<string, unknown>;
  account: string;
  accountLabel?: string;
  organizationLabel?: string;
  organizationId: string;
  threadId: string;
  expiresAt: string;
  digest: string;
  status: 'pending' | 'running' | 'executed' | 'rejected' | 'superseded' | 'failed' | 'expired';
  decision: ActionDecision;
  canDecide: boolean;
  editing?: boolean;
  presentation?: 'inline' | 'canvas';
  messageApproval?: { allowed: boolean; tier: 'low' | 'medium' | 'high' };
  approval?:
    | { method: 'request'; messageId: string }
    | {
        method: 'message' | 'card';
        messageId: string;
        text: string;
        model: string;
        version: string;
        score: number;
      };
  result?: unknown;
  error?: string;
  /** Owner-authorized origin of the assistant answer that staged this action. */
  answerMessageId?: string;
};

export function isActionPointer(value: unknown): value is ActionPointer {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as { status?: unknown; action?: { id?: unknown } };
  return (
    (candidate.status === 'confirmation_required' ||
      candidate.status === 'confirmation_already_pending' ||
      candidate.status === 'build_requested') &&
    typeof candidate.action?.id === 'string' &&
    candidate.action.id.length > 0 &&
    !candidate.action.id.startsWith('sva_')
  );
}

export function ActionConfirmationCard(props: ActionConfirmationProps) {
  const workspace = useOptionalWorkspace();
  const copy = useChatCopy();
  const surface = actionSurfaceCopy(useChatInterfaceLocale());
  // Shared readers have no authority to fetch the private approval receipt.
  // Render an inert indication from already-readable thread metadata only.
  if (workspace?.readOnly)
    return (
      <section className="pc-action-status-line" role="status">
        {surface.waitingFor(workspace.threadMeta?.ownerName ?? copy.account)}
      </section>
    );
  return <OwnedActionConfirmation {...props} />;
}

/** A successful restage, from the request that was edited to the one that replaced it. */
export type ActionRestage = { from: string; to: string };

type ActionConfirmationProps = {
  pointer: ActionPointer;
  mode?: 'compact' | 'canvas';
  fresh?: boolean;
  origin?: ActionOrigin;
  /** Canvas only: the last restage the canvas saw succeed, and how to report one. */
  restage?: ActionRestage | null;
  onRestaged?: (restage: ActionRestage) => void;
};
function OwnedActionConfirmation({
  pointer,
  mode = 'compact',
  fresh = false,
  origin,
  restage = null,
  onRestaged,
}: ActionConfirmationProps) {
  const state = useActionConfirmation(pointer, mode, fresh, origin, onRestaged);
  // Fixed at mount: only rows that arrive with the live turn animate in. An
  // older thread reopened later renders its history still.
  const [enteredLive] = useState(fresh);
  const { action, loadError, surfaceCopy } = state;
  if (!action && !loadError)
    return (
      <section className="pc-action-status-line" role="status">
        {surfaceCopy.loading}
      </section>
    );
  if (loadError)
    return (
      <section className="pc-action-status-line" role="status">
        {loadError.status === 403 || loadError.status === 404
          ? surfaceCopy.denied
          : surfaceCopy.uncertain}
      </section>
    );
  if (!action) return null;
  return (
    <div
      ref={state.presentation.ref}
      className={`pc-action-host pc-action-host-${mode}`}
      data-action-id={action.id}
      data-fresh={enteredLive ? 'true' : undefined}
    >
      {mode === 'compact' || state.requestedBuild ? (
        <ActionInline state={state} action={action} />
      ) : (
        <ActionCanvasReview
          state={state}
          action={action}
          restagedWithEdits={restage?.to === action.id}
        />
      )}
    </div>
  );
}

/** Equivalent tool retries point back to one pending card instead of duplicating controls. */
export function ActionConfirmationReference({ pointer }: { pointer: ActionPointer }) {
  const workspace = useOptionalWorkspace();
  const copy = actionCopy(useChatInterfaceLocale());
  if (pointer.status === 'build_requested') return <ActionConfirmationCard pointer={pointer} />;
  return (
    <Button
      type="button"
      variant="ghost"
      className="min-h-11"
      onClick={() => workspace?.openCanvas({ kind: 'action', id: pointer.action.id })}
    >
      {copy.open} · {copy.review}
    </Button>
  );
}
